// # ui-no-screenshot-needed: sign-in screen follows the upstream layout; the hosted platform's Google and Microsoft social sign-in is replaced by Microsoft Entra through MSAL plus the local development sign-in, which cannot render until the API is up
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AppProvider } from "@/components/shell/AppContext";
import { useAuth } from "@/infra/auth/AuthProvider";
import { HOME_PATH } from "@/components/shell/nav";
import { signInConfig } from "@/services/tenancy";
import { queryKeys } from "@/services/queryKeys";
import suppliedLogo from "@/assets/oxley-logo.svg.asset.json";
import microsoftLogo from "@/assets/microsoft-provider.svg.asset.json";
import { ArrowRight, Boxes, CheckCircle2, Layers3, PackageOpen, PlusCircle, ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/auth")({
  validateSearch: (search: Record<string, unknown>): { demo?: boolean } => ({
    demo: search.demo !== undefined ? true : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Sign in · Oxley" },
      { name: "description", content: "Sign in to Oxley to design, approve and publish how you package your products." },
      { property: "og:title", content: "Sign in · Oxley" },
      { property: "og:description", content: "Sign in to Oxley to design, approve and publish how you package your products." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: () => (
    <AppProvider>
      <AuthPage />
    </AppProvider>
  ),
});

const EDITIONS = ["Professional", "Enterprise", "Unlimited", "AI Edition"];
const ROWS: { name: string; cells: ("in" | "add" | "")[] }[] = [
  { name: "Opportunity Management", cells: ["in", "in", "in", "in"] },
  { name: "Workflow Automation", cells: ["", "in", "in", "in"] },
  { name: "Cadences", cells: ["", "add", "in", "in"] },
  { name: "Agent Actions", cells: ["", "", "add", "in"] },
  { name: "AI Deal Scoring", cells: ["", "", "add", "in"] },
];

const POINTS = [
  { icon: Boxes, title: "Catalog", text: "Product lines, products and features in one place." },
  { icon: Layers3, title: "Packaging", text: "Editions on a clear ladder, plus paid add-ons." },
  { icon: ShieldCheck, title: "Approvals", text: "Packaging rules check every change before it is published." },
];

function PackagingPreview() {
  return (
    <div aria-hidden="true" className="overflow-hidden rounded-lg border border-border bg-card text-foreground shadow-lift">
      <div className="flex items-center gap-2 bg-navy px-4 py-2.5 text-rail-foreground">
        <span className="text-[14px] font-semibold">Sales Cloud</span>
        <span className="ml-3 inline-flex items-center gap-1.5 border-b-2 border-brand-mint pb-0.5 text-[12px] font-semibold text-brand-mint">
          <PackageOpen className="h-3.5 w-3.5" />
          Packaging
        </span>
      </div>
      <div className="grid grid-cols-[1.6fr_repeat(4,1fr)] border-b border-border bg-navy-soft text-[11px] font-semibold">
        <div className="px-3 py-2">Features</div>
        {EDITIONS.map((e) => (
          <div key={e} className="px-2 py-2 text-center">{e}</div>
        ))}
      </div>
      {ROWS.map((row) => (
        <div key={row.name} className="grid grid-cols-[1.6fr_repeat(4,1fr)] border-b border-border text-[11px] last:border-b-0">
          <div className="truncate px-3 py-2 text-muted-foreground">{row.name}</div>
          {row.cells.map((c, i) => (
            <div key={i} className="grid place-items-center py-2">
              {c === "in" && <CheckCircle2 className="h-3.5 w-3.5 text-success" />}
              {c === "add" && <PlusCircle className="h-3.5 w-3.5 text-navy" />}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function AuthPage() {
  const { signInWithMicrosoft, signInLocal, claims, ready } = useAuth();
  const navigate = useNavigate();
  const cfg = useQuery({ queryKey: queryKeys.signInConfig(), queryFn: signInConfig, staleTime: Infinity });
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (ready && claims) navigate({ to: HOME_PATH, replace: true });
  }, [ready, claims, navigate]);

  // When Microsoft refuses the account and sends the person back with an error, say so plainly.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const h = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const desc = q.get("error_description") ?? h.get("error_description") ?? q.get("error") ?? h.get("error");
    if (desc) setError(`Microsoft sign-in did not complete: ${desc}`);
  }, []);

  async function microsoft() {
    setBusy(true);
    setError(null);
    const r = await signInWithMicrosoft(email || undefined);
    if (r.error) {
      setError(`Microsoft sign-in did not complete: ${r.error}`);
      setBusy(false);
    }
  }

  async function local(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await signInLocal(email);
    } catch {
      toast.error("That email could not be signed in. Check the API is running with DEV_LOGIN=true.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-screen bg-background lg:grid-cols-[1.1fr_1fr]">
      <section className="auth-brand-panel relative hidden flex-col items-center justify-between gap-10 overflow-hidden px-8 py-10 text-center text-primary-foreground lg:flex xl:px-12">
        <div className="flex items-center gap-3">
          <img src={suppliedLogo.url} alt="" width={56} height={56} className="h-14 w-14" />
          <span className="text-[26px] font-bold text-brand-mint">Oxley</span>
        </div>
        <div className="w-full max-w-xl">
          <h2 className="mx-auto max-w-lg text-[34px] font-semibold leading-tight">Your products and packaging in one place.</h2>
          <p className="mx-auto mt-4 max-w-lg text-[16px] leading-relaxed text-primary-foreground/80">
            Design editions and add-ons, check them against your packaging rules, get approval and publish, with an assistant that guides every step.
          </p>
          <div className="mt-8"><PackagingPreview /></div>
          <ul className="mt-8 grid grid-cols-3 gap-4">
            {POINTS.map(({ icon: Icon, title, text }) => (
              <li key={title} className="flex flex-col items-center">
                <Icon className="h-5 w-5 text-brand-mint" aria-hidden="true" />
                <div className="mt-2 text-[14px] font-semibold">{title}</div>
                <p className="mt-1 text-[13px] leading-snug text-primary-foreground/75">{text}</p>
              </li>
            ))}
          </ul>
        </div>
        <p className="text-[14px] font-medium text-brand-mint">Oxley suggests. You decide. Rules keep it right.</p>
      </section>

      <section className="auth-signin-panel flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="mb-10 flex items-center gap-3 lg:hidden">
            <img src={suppliedLogo.url} alt="" width={48} height={48} className="h-12 w-12 rounded-md bg-navy" />
            <span className="text-[22px] font-bold text-navy">Oxley</span>
          </div>
          <div className="rounded-lg border border-border bg-card p-8 text-center shadow-lift sm:p-10">
            <h1 className="text-[26px] font-semibold text-foreground">Sign in</h1>
            <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">Use your work account to continue.</p>
            <div className="mt-7 space-y-3">
              <Button variant="outline" className="h-12 w-full justify-start gap-3 bg-card px-4 text-[15px]" disabled={busy || !ready} onClick={microsoft}>
                <img src={microsoftLogo.url} alt="" width={20} height={20} className="h-5 w-5 shrink-0" />
                <span className="flex-1 text-left">{busy ? "Signing in..." : "Continue with Microsoft"}</span>
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Button>
              {cfg.data && !cfg.data.configured && !cfg.data.dev_login && (
                <p className="text-[13px] text-warn">Microsoft sign-in is not set up for this environment yet.</p>
              )}
              {error && <p className="text-[13px] text-danger">{error}</p>}
            </div>

            {cfg.data?.dev_login && (
              <>
                <div className="my-6 flex items-center gap-3 text-[12px] text-muted-foreground">
                  <span className="h-px flex-1 bg-border" />
                  Local development sign-in
                  <span className="h-px flex-1 bg-border" />
                </div>
                <form onSubmit={local} className="space-y-4 text-left">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email address</Label>
                    <Input className="h-11 bg-background" id="email" type="email" placeholder="name@company.com" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                  </div>
                  <Button type="submit" size="lg" variant="outline" className="w-full" disabled={busy}>
                    {busy ? "Signing in..." : <>Continue without Microsoft <ArrowRight className="h-4 w-4" aria-hidden="true" /></>}
                  </Button>
                  <p className="text-center text-[12px] text-muted-foreground">Only available on a local machine. Roles still come from your organization.</p>
                </form>
              </>
            )}

            <p className="mt-6 border-t border-border pt-5 text-[13px] leading-relaxed text-muted-foreground">
              Oxley uses your organization's sign-in provider. If you need access, contact your Admin.
            </p>
          </div>
          <p className="mt-6 text-center text-[13px] font-medium text-muted-foreground lg:hidden">Oxley suggests. You decide. Rules keep it right.</p>
        </div>
      </section>
    </main>
  );
}
